import React, { useState, useEffect } from 'react';
import { useBooks } from './hooks/useBooks';
import { useReadingLogs } from './hooks/useReadingLogs';
import { ReadingLogList } from './components/ReadingLogList';
import { EnhancedBookFilter } from './components/EnhancedBookFilter';
import { LoginForm } from './components/LoginForm';
import { Book, cleanupOrphanedReadingLogs } from './lib/nocodb';
import { authUtils } from './utils/auth';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<string | null>(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  
  const { books, error: booksError, addBook, refetchBooks, removeBook } = useBooks();
  const { readingLogs, loading: logsLoading, error: logsError, addReadingLog, removeReadingLog, removeMultipleReadingLogs, refetchReadingLogs } = useReadingLogs();
  const [selectedBook, setSelectedBook] = useState<Book | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [triggerAddLog, setTriggerAddLog] = useState(false);

  // Cleanup orphaned reading logs on startup
  useEffect(() => {
    const performCleanup = async () => {
      try {
        const result = await cleanupOrphanedReadingLogs();
        if (result.deletedCount > 0) {
          console.log(`🧹 Cleaned up ${result.deletedCount} orphaned reading logs`);
          // Refresh reading logs after cleanup
          refetchReadingLogs();
        }
      } catch (error) {
        console.error('Failed to cleanup orphaned logs:', error);
      }
    };

    // Run cleanup immediately and again after app loads to clean up any existing orphaned entries
    performCleanup(); // Run immediately
    const timeoutId = setTimeout(performCleanup, 3000); // Run again after 3 seconds
    return () => clearTimeout(timeoutId);
  }, [refetchReadingLogs]);

  // Check authentication status on app load
  useEffect(() => {
    const checkAuth = () => {
      const authenticated = authUtils.isAuthenticated();
      const currentUser = authUtils.getUser();
      
      setIsAuthenticated(authenticated);
      setUser(currentUser);
      setIsCheckingAuth(false);
    };

    checkAuth();
  }, []);


  const handleBookAdded = async () => {
    await refetchBooks();
  }

  const handleAddLog = async (book_id: number, notes: string) => {
    const bookData = books.find(book => book.Id === book_id);
    await addReadingLog(book_id, notes, bookData);
  };

  const handleLogin = async (username: string, password: string, rememberMe: boolean): Promise<boolean> => {
    const success = await authUtils.login(username, password, rememberMe);
    if (success) {
      setIsAuthenticated(true);
      setUser(username);
    }
    return success;
  };

  const handleLogout = () => {
    authUtils.logout();
    setIsAuthenticated(false);
    setUser(null);
    setSelectedBook(null);
    setSearchQuery('');
  };

  const handleAddBook = async (title: string, author: string, coverUrl?: string, publicationYear?: number) => {
    const newBook = await addBook(title, author, coverUrl, publicationYear);
    handleBookAdded();
    return newBook;
  }

  const handleDeleteBook = async (bookId: number) => {
    try {
      // First delete all logs for this book
      const bookLogs = readingLogs.filter(log => {
        const bookInfo = getBookInfo(log);
        return bookInfo.id === bookId;
      });
      
      if (bookLogs.length > 0) {
        await removeMultipleReadingLogs(bookLogs.map(log => log.Id));
      }
      
      // Then delete the book
      await removeBook(bookId);
      
      // Clear selection if this book was selected
      if (selectedBook?.Id === bookId) {
        setSelectedBook(null);
      }
    } catch (error) {
      console.error('Failed to delete book:', error);
      throw error;
    }
  };

  // Helper function to get book info (copied from ReadingLogList)
  const getBookInfo = (log: any) => {
    if (log._localBookData) {
      return {
        title: log._localBookData.title,
        author: log._localBookData.author,
        id: log._localBookData.Id
      };
    }
    
    const bookData = log._nc_m2m_reading_log_books?.[0]?.books;
    if (bookData) {
      return {
        title: bookData.title,
        author: bookData.author,
        id: bookData.Id
      };
    }
    
    return {
      title: 'Unknown Book',
      author: 'Unknown Author',
      id: null
    };
  };

  // Show loading screen while checking authentication
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 text-blue-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show login form if not authenticated
  if (!isAuthenticated) {
    return <LoginForm onLogin={handleLogin} />;
  }

  return (
    <div className="bg-gray-50 min-h-screen font-sans">
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold text-gray-800">My Reading Log</h1>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">Welcome, {user}</span>
              <button
                onClick={handleLogout}
                className="px-3 py-1 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>
      <main className="container mx-auto px-4 py-8">
        {(booksError || logsError) && (
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6" role="alert">
            <p className="font-bold">Oops! Something went wrong.</p>
            <p>{booksError?.message || logsError?.message}</p>
          </div>
        )}
        
        {logsLoading ? (
          <p>Loading logs...</p>
        ) : (
          <div className="space-y-6">
            {/* Enhanced Book Filter and Controls */}
            <EnhancedBookFilter 
              books={books}
              selectedBook={selectedBook}
              searchQuery={searchQuery}
              onBookSelect={setSelectedBook}
              onSearchChange={setSearchQuery}
              onAddBook={handleAddBook}
              onAddLog={handleAddLog}
              onDeleteBook={handleDeleteBook}
              triggerAddLog={triggerAddLog}
              onTriggerAddLogHandled={() => setTriggerAddLog(false)}
            />
              
              <ReadingLogList 
                readingLogs={readingLogs} 
                selectedBook={selectedBook}
                searchQuery={searchQuery}
                onDeleteLog={removeReadingLog}
                onDeleteMultipleLogs={removeMultipleReadingLogs}
                onBookSelect={setSelectedBook}
                books={books}
                onAddLog={() => setTriggerAddLog(true)}
                onDeleteBook={() => selectedBook && handleDeleteBook(selectedBook.Id)}
              />
          </div>
        )}
      </main>
    </div>
  );
}

export default App;